import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Keeps the current user's effective permissions fresh in real time.
 *
 * When an administrator toggles a permission on the role this user is assigned
 * to, the `role_permissions` change is streamed here and the cached
 * `['user-permissions']` query is invalidated immediately, instead of waiting
 * for the 60s staleTime fallback in `useUserPermissions`.
 *
 * RLS ("Users can view own role permissions") already restricts the stream to
 * the caller's own role; the `role_id` filter narrows it further so unrelated
 * role edits do not wake this subscription.
 *
 * Mounted once in `AuthenticatedRoutesLayout` to avoid subscription churn.
 */
export function useRolePermissionsRealtime() {
  const { profile } = useAuth();
  const roleId = profile?.role_id;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!roleId) return;

    const channel = supabase
      .channel(`role-permissions-${roleId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'role_permissions',
          filter: `role_id=eq.${roleId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
          queryClient.invalidateQueries({ queryKey: ['role-permissions', roleId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roleId, queryClient]);
}
